import { useEffect, useMemo, useRef, useState } from "react";
import StreamVideo from "@/components/StreamVideo";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WatchPartyGate from "@/components/WatchPartyGate";
import useWatchPartyChat from "@/hooks/useWatchPartyChat";
import useVisualViewport from "@/hooks/useVisualViewport";
import usePlaybackSync from "@/hooks/usePlaybackSync";
import { SyncStatus, TapToJoin } from "@/components/WatchPartySync";
import WatchPartyFull from "@/components/WatchPartyFull";
import LaunchCountdown from "@/components/LaunchCountdown";
import { EndPartyButton, WatchPartyEnded } from "@/components/WatchPartyEnd";
import { ReactionBar, StatusPill, FloatingReactions } from "@/components/WatchPartyLive";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  MessageSquare,
  Users,
  Clock,
  Loader2,
  Send,
  XCircle,
  Sparkles,
  UserPlus,
  Check,
  Maximize2,
  Minimize2,
  ChevronRight,
} from "lucide-react";

// Poll cadence for chat — long enough to be light on the server and
// gentle on batteries, short enough to feel real-time for a small group.

/**
 * Public Watch Party viewer. Guests hit this page with the shared link
 * — no login required. Renders the film video (streamed via the token-
 * gated endpoint) plus a live WebSocket chat sidebar (polling fallback).
 * Each viewer plays the film independently on their own device; there is
 * no sync layer. State machine:
 *   loading → valid | expired | not-found
 */
export default function WatchPartyView() {
  const { token } = useParams();
  const { user } = useAuth();
  const name = user?.name || "Guest";
  const [party, setParty] = useState(null);
  const [error, setError] = useState(null);
  const [endedFilmId, setEndedFilmId] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistName, setWaitlistName] = useState(user?.name || "");
  const [waitlistEmail, setWaitlistEmail] = useState(user?.email || "");
  const [waitlistSent, setWaitlistSent] = useState(false);
  // Cinema-mode: fullscreens the container (not the <video> itself) so
  // the floating chat pill stays in-frame. Browsers strip children from
  // the DOM when you fullscreen the raw video element — fullscreening
  // the wrapper preserves them.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chatOpen, setChatOpen] = useState(true); // controls floating chat in fullscreen
  const stageRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const jumpedRef = useRef(false);

  // Fetch party details once on mount.
  useEffect(() => {
    api.get(`/watch-party/join/${token}`).then((r) => {
      setParty(r.data);
    }).catch((e) => {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 410 && detail?.code === "ended") { setEndedFilmId(detail.film_id); setError("ended"); }
      else if (status === 404) setError("not-found");
      else if (status === 410) setError("expired");
      else if (status === 402) setError("pending");
      else setError("unknown");
    });
  }, [token]);

  // Claim a seat in the host's package before loading the room; 409 = every seat taken.
  const [seated, setSeated] = useState(false);
  useEffect(() => {
    if (!party || !user || party.launch_waiting) return;
    api.post(`/watch-party/${token}/seat`).then(() => setSeated(true)).catch((e) => {
      const st = e?.response?.status;
      setError(st === 409 ? "full" : st === 410 ? "expired" : "unknown");
    });
  }, [party, user, token]);

  const { msgs, merge, status, watching, ended, lastReaction, isHost, hostOnline, lastSync, sendSync, serverNow } = useWatchPartyChat(token, !!party && !!user && seated);
  const [videoEl, setVideoEl] = useState(null);
  const sync = usePlaybackSync({ video: videoEl, isHost, live: status === "live", lastSync, hostOnline, sendSync, serverNow });
  useEffect(() => { if (ended) setError(ended === "expired" ? "expired" : "ended"); }, [ended]);

  const vp = useVisualViewport(!!party && !!user && seated && !error);
  const kb = vp.keyboardOpen;

  // Scroll only the chat log (scrollIntoView would also scroll the page on iOS).
  useEffect(() => {
    const box = chatBoxRef.current;
    if (!box) return;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (!jumpedRef.current && msgs.length) { jumpedRef.current = true; box.scrollTop = box.scrollHeight; }
    else if (nearBottom) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  }, [msgs, party, user]);

  // Keep the latest message visible when the keyboard opens/closes.
  useEffect(() => {
    const box = chatBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [vp.height]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const msg = draft.trim();
    if (!msg) return;
    setSending(true);
    setDraft("");
    try {
      const { data } = await api.post(`/watch-party/${token}/chat`, { message: msg });
      merge([data]);
    } catch { /* toast? keep it quiet — chat will refresh on next poll */ }
    setSending(false);
  };

  const submitWaitlist = async (e) => {
    e?.preventDefault?.();
    if (!waitlistName.trim() || !waitlistEmail.includes("@")) return;
    try {
      await api.post(`/watch-party/${token}/waitlist`, { name: waitlistName.trim(), email: waitlistEmail.trim() });
      setWaitlistSent(true);
    } catch { /* toast? keep quiet in guest UI */ }
  };

  // Only real chat messages render in the log.
  const chatMsgs = useMemo(() => msgs.filter((m) => m.kind !== "reaction"), [msgs]);

  // Keep our fullscreen flag in sync with the browser — Escape / F11 /
  // Safari controls can all exit fullscreen without our toggle firing.
  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const toggleFullscreen = async () => {
    const stage = stageRef.current;
    if (!stage) return;
    try {
      if (!document.fullscreenElement) {
        // Prefer the standard API; fall back to WebKit for iOS Safari.
        const req = stage.requestFullscreen || stage.webkitRequestFullscreen;
        if (req) await req.call(stage);
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch { /* Safari sometimes rejects silently — the toggle stays valid */ }
  };

  // ------- Error states -------
  if (party?.launch_waiting) return <LaunchCountdown party={party} token={token} user={user} />;
  if (error === "full") return <WatchPartyFull party={party} token={token} user={user} />;
  if (error === "ended") return <WatchPartyEnded filmId={party?.film_id || endedFilmId} immediate={isHost} />;
  if (error) {
    return (
      <div className="min-h-screen bg-ink grain flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <XCircle className="w-14 h-14 mx-auto text-red-400 mb-4" />
          <h1 className="font-display text-3xl font-black mb-2">
            {error === "expired" && "This Watch Party has ended"}
            {error === "not-found" && "Watch Party not found"}
            {error === "pending" && "Payment not yet confirmed"}
            {error === "unknown" && "Something went wrong"}
          </h1>
          <p className="text-foreground/60 text-sm">
            {error === "expired" && "The 24-hour session is over. Ask the host to spin up a new party."}
            {error === "not-found" && "Double-check the link — it might be typo'd or the host cancelled."}
            {error === "pending" && "The host just paid — give it a few seconds and refresh."}
            {error === "unknown" && "Try refreshing the page in a moment."}
          </p>
        </div>
      </div>
    );
  }

  if (!party || user === null) {
    return (
      <div className="min-h-screen bg-ink grain flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
      </div>
    );
  }

  if (!user) return <WatchPartyGate party={party} token={token} />;

  if (!seated) {
    return (
      <div className="min-h-screen bg-ink grain flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-terracotta animate-spin" />
      </div>
    );
  }

  const expiryLabel = (() => {
    if (!party.expires_at) return "";
    const diff = new Date(party.expires_at).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
  })();

  return (
    <div
      className="fixed inset-x-0 top-0 bg-ink grain flex flex-col overflow-hidden overscroll-none"
      style={{ height: vp.height, top: vp.offsetTop }}
      data-testid="watch-party-view"
    >
      {/* Slim header — collapsed on mobile while the keyboard is open to leave room for messages */}
      <header className={`px-4 sm:px-6 py-3 items-center justify-between border-b border-white/10 bg-black/40 backdrop-blur shrink-0 ${kb ? "hidden lg:flex" : "flex"}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Logo mark size={30} />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-terracotta font-semibold flex items-center gap-1">
              <Users className="w-3 h-3" /> Watch Party
            </p>
            <p className="text-sm font-display font-bold truncate" data-testid="wp-title">{party.film_title}</p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-[10px] uppercase tracking-widest text-foreground/50">Hosted by</p>
          <p className="text-sm font-semibold">{party.host_name}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-gold" data-testid="wp-expiry">
            <Clock className="w-3.5 h-3.5" /> {expiryLabel}
          </div>
          {isHost && <EndPartyButton token={token} filmId={party.film_id} />}
        </div>
      </header>

      {/* Body: video + chat side-by-side on lg+, stacked on smaller.
          On mobile we constrain the video height so the chat below
          stays independently scrollable and the video never gets
          pushed off-screen. */}
      <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-[1fr_360px] gap-0">
        {/* Video stage — this whole container is what we fullscreen
            (not the <video>) so the floating chat pill remains visible
            in cinema mode. Mobile uses a fixed 16:9 aspect box so the
            video height is derived from screen WIDTH, not viewport
            height — the keyboard opening (which shrinks 100dvh) can't
            resize or push the video off screen. Desktop lets the
            grid row size it naturally. */}
        <div
          ref={stageRef}
          className="relative bg-black flex items-center justify-center shrink-0 w-full aspect-video lg:aspect-auto lg:h-auto lg:min-h-[240px]"
          data-testid="wp-video-stage"
        >
          <FloatingReactions latest={lastReaction} />
          <SyncStatus sync={sync} />
          {sync.needsTap && <TapToJoin onTap={sync.tapToJoin} isHost={isHost} />}
          <StreamVideo
            ref={setVideoEl}
            playbackPath={`/watch-party/${token}/playback`}
            legacySrc={`${API}/watch-party/${token}/video`}
            controls
            playsInline
            className="w-full h-full object-contain"
            data-testid="wp-video"
          />
          {/* Fullscreen toggle — discreet glass pill top-right of the
              stage. Doesn't move to the browser chrome so guests always
              know how to exit cinema mode. */}
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-black/60 backdrop-blur border border-white/15 flex items-center justify-center text-white/85 hover:text-white hover:bg-black/80 transition-colors"
            data-testid="wp-fullscreen-toggle"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Floating chat overlay — only rendered inside the stage
              container in fullscreen mode. Two states: collapsed handle
              (pill tab on the right edge) → expanded panel with a live
              feed of the last 8 messages. Only shown when isFullscreen
              so the standard side chat handles everything otherwise. */}
          {isFullscreen && (
            <>
              {!chatOpen && (
                <button
                  type="button"
                  onClick={() => setChatOpen(true)}
                  className="absolute top-1/2 right-0 -translate-y-1/2 z-20 flex items-center gap-1.5 pl-2 pr-3 py-2 rounded-l-full bg-black/70 backdrop-blur border border-r-0 border-white/15 text-white/90 hover:text-white hover:bg-black/85"
                  data-testid="wp-fs-chat-open"
                  aria-label="Open chat"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">{chatMsgs.length}</span>
                </button>
              )}
              {chatOpen && (
                <div
                  className="absolute top-4 right-4 bottom-20 z-20 w-[300px] max-w-[85vw] rounded-2xl bg-black/70 backdrop-blur-md border border-white/15 flex flex-col overflow-hidden shadow-2xl"
                  data-testid="wp-fs-chat-panel"
                >
                  <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-terracotta" />
                    <p className="text-xs font-display font-bold">Party chat</p>
                    <span className="text-[10px] text-foreground/50 ml-auto">{chatMsgs.length}</span>
                    <button
                      type="button"
                      onClick={() => setChatOpen(false)}
                      className="w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-foreground/70"
                      aria-label="Collapse chat"
                      data-testid="wp-fs-chat-collapse"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 min-h-0 text-xs">
                    {chatMsgs.length === 0 ? (
                      <p className="text-foreground/50 text-center py-4">Say hi.</p>
                    ) : (
                      chatMsgs.slice(-40).map((m) => (
                        <div key={m.id} className="leading-snug">
                          <span className={`font-semibold ${m.sender_user_id === user.id ? "text-terracotta" : "text-gold"}`}>
                            {m.sender_name}
                          </span>
                          <span className="text-foreground/90 ml-1.5">{m.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                    <form onSubmit={sendMessage} className="p-2 border-t border-white/10 flex items-center gap-1.5" data-testid="wp-fs-chat-form">
                      <Input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Message…"
                        maxLength={500}
                        className="bg-white/5 border-white/10 h-8 text-xs"
                        data-testid="wp-fs-chat-input"
                      />
                      <Button
                        type="submit"
                        size="icon"
                        disabled={sending || !draft.trim()}
                        className="h-8 w-8 bg-terracotta hover:bg-ember text-white shrink-0"
                      >
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    </form>
                </div>
              )}
            </>
          )}
        </div>

        {/* Chat — fills remaining space on mobile (flex-1 min-h-0) so
            the inner message log scrolls independently; fills the
            grid row on desktop. */}
        <aside className="border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col bg-surface/60 flex-1 min-h-0 lg:h-full lg:flex-none">
          <div className={`px-4 py-3 border-b border-white/10 items-center gap-2 shrink-0 ${kb ? "hidden lg:flex" : "flex"}`}>
            <MessageSquare className="w-4 h-4 text-terracotta" />
            <p className="text-sm font-display font-bold">Party chat</p>
            <span className="ml-auto"><StatusPill status={status} watching={watching} /></span>
          </div>

          {/* Waitlist bar — if the party feels full, guests can ask the
              host to bump the cap. Hidden once submitted (locally); the
              host receives an email via Resend. */}
          {!waitlistSent && !kb && (
            <div className="px-4 py-2 border-b border-white/10 bg-black/20 flex items-center gap-2 text-xs shrink-0">
              <UserPlus className="w-3.5 h-3.5 text-gold shrink-0" />
              <span className="text-foreground/70 truncate">Party feels full?</span>
              <button
                type="button"
                onClick={() => setShowWaitlist((v) => !v)}
                className="ml-auto text-terracotta hover:text-ember font-semibold shrink-0"
                data-testid="wp-waitlist-toggle"
              >
                {showWaitlist ? "Cancel" : "Ask host for a seat"}
              </button>
            </div>
          )}
          {waitlistSent && (
            <div className="px-4 py-2 border-b border-white/10 bg-emerald-500/10 text-xs text-emerald-300 flex items-center gap-2 shrink-0" data-testid="wp-waitlist-sent">
              <Check className="w-3.5 h-3.5" /> Host has been notified — hold tight.
            </div>
          )}
          {showWaitlist && !waitlistSent && (
            <form
              onSubmit={submitWaitlist}
              className="px-4 py-3 border-b border-white/10 bg-black/30 space-y-2 shrink-0"
              data-testid="wp-waitlist-form"
            >
              <Input
                value={waitlistName}
                onChange={(e) => setWaitlistName(e.target.value)}
                placeholder="Your name"
                maxLength={60}
                className="bg-white/5 border-white/10 h-9 text-sm"
                data-testid="wp-waitlist-name"
              />
              <Input
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="Email so the host can reach you"
                type="email"
                maxLength={120}
                className="bg-white/5 border-white/10 h-9 text-sm"
                data-testid="wp-waitlist-email"
              />
              <Button
                type="submit"
                disabled={!waitlistName.trim() || !waitlistEmail.includes("@")}
                className="w-full h-9 bg-terracotta hover:bg-ember text-white text-sm"
                data-testid="wp-waitlist-submit"
              >
                Notify host
              </Button>
            </form>
          )}

          <div
            ref={chatBoxRef}
            className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 space-y-2.5 min-h-0"
            data-testid="wp-chat-log"
          >
            {chatMsgs.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-5 h-5 mx-auto text-terracotta/60 mb-2" />
                <p className="text-xs text-foreground/50 leading-relaxed">
                  Chat is quiet — say hi to kick things off.
                </p>
              </div>
            )}
            {chatMsgs.map((m) => (
              <div key={m.id} className="text-sm leading-snug" data-testid={`wp-chat-msg-${m.id}`}>
                <span className={`font-semibold ${m.sender_user_id === user.id ? "text-terracotta" : "text-gold"}`}>
                  {m.sender_name}
                </span>
                <span className="text-foreground/90 ml-2">{m.message}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {!kb && <ReactionBar token={token} onSent={(d) => merge([d], { live: true })} />}
          <div className="p-3 border-t border-white/10 shrink-0 bg-surface/60" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
              <form onSubmit={sendMessage} className="flex items-center gap-2" data-testid="wp-chat-form">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={`Message as ${name}…`}
                  maxLength={500}
                  className="bg-white/5 border-white/10 h-10 text-base lg:text-sm"
                  data-testid="wp-chat-input"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={sending || !draft.trim()}
                  className="h-10 w-10 bg-terracotta hover:bg-ember text-white shrink-0"
                  data-testid="wp-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
